import { FC, useState } from "react";
import styled from "@emotion/styled";
import { useConvex, useQuery } from "convex/react";
import { useAppStore } from "@/store";
import { logger } from "@/modules/logger";
import { api } from "@/smart-views/client/api";
import { createSmartView } from "@/smart-views/client/backend-api";
import { runSuggestionAgent } from "@/smart-views/agent/runSuggestionAgent";
import { isConvexConfigured } from "@/smart-views/client/convex-client";
import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSDialogSlotName,
  MDSModalDialog,
  MDSText,
  MDSTextColor,
  MDSTextField,
  MDSTextVariant,
} from "@/mds";

interface CreateSmartViewModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateSmartViewModal: FC<CreateSmartViewModalProps> = ({ isOpen, onClose }) => {
  const { store } = useAppStore();
  const convex = useConvex();
  const smartViewsEnabled = isConvexConfigured();
  const suggestionSet = useQuery(api.suggestions.latest, isOpen && smartViewsEnabled ? {} : "skip");
  const suggestionsLoading = isOpen && smartViewsEnabled && suggestionSet === undefined;
  const [title, setTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const close = () => {
    setTitle("");
    setSubmitting(false);
    onClose();
  };

  const submit = async (override?: { title: string; seedHint?: string }) => {
    const trimmed = (override?.title ?? title).trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      const response = await createSmartView({
        title: trimmed,
        description: override?.seedHint,
      });
      store.navigation.goToSmartView({ smartViewId: response.smart_view_id });
      close();
      void runSuggestionAgent({ store, convex, force: true });
    } catch (err) {
      logger.error({ message: "Failed to create smart view", info: { err: String(err) } });
      setSubmitting(false);
    }
  };

  return (
    <StyledDialog isOpen={isOpen} handleClose={close}>
      <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
        Name your Smart View
      </MDSText>
      <div slot={MDSDialogSlotName.Content}>
        <MDSTextField
          value={title}
          onChange={value => setTitle(value)}
          placeholder="e.g. Acme Co deal"
          inputProps={{
            onKeyDown: e => {
              if (e.key === "Enter") {
                e.preventDefault();
                void submit();
              }
            },
          }}
          autoFocus
        />
        <Suggestions>
          {suggestionsLoading && (
            <SuggestionStatus>
              <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Tertiary}>
                Looking at your recent notes for ideas...
              </MDSText>
            </SuggestionStatus>
          )}
          {isOpen && suggestionSet === null && (
            <SuggestionStatus>
              <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Tertiary}>
                Suggestions unavailable right now.
              </MDSText>
            </SuggestionStatus>
          )}
          {suggestionSet && suggestionSet.suggestions.length > 0 && (
            <>
              <SuggestionHeader>
                <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Tertiary}>
                  Or pick a suggestion
                </MDSText>
              </SuggestionHeader>
              <SuggestionChips>
                {suggestionSet.suggestions.map(suggestion => (
                  <SuggestionChip
                    key={suggestion.title}
                    type="button"
                    title={suggestion.rationale}
                    onClick={() => {
                      setTitle(suggestion.title);
                      void submit({
                        title: suggestion.title,
                        seedHint: suggestion.rationale,
                      });
                    }}
                  >
                    {suggestion.title}
                  </SuggestionChip>
                ))}
              </SuggestionChips>
            </>
          )}
        </Suggestions>
      </div>
      <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
        <MDSButton onClick={close}>Cancel</MDSButton>
        <MDSButton
          variant={MDSButtonVariant.Contrast}
          isDisabled={!title.trim() || submitting}
          onClick={() => void submit()}
        >
          Create
        </MDSButton>
      </MDSButtonGroup>
    </StyledDialog>
  );
};

const StyledDialog = styled(MDSModalDialog)`
  .mds-dialog {
    padding: ${({ theme }) => theme.spacing[7]};
    width: min(560px, 92vw);
  }
`;

const Suggestions = styled.div`
  margin-top: ${({ theme }) => theme.spacing["4"]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const SuggestionStatus = styled.div`
  padding: ${({ theme }) => theme.spacing["2"]} 0;
`;

const SuggestionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  text-transform: uppercase;
`;

const SuggestionChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const SuggestionChip = styled.button`
  padding: 6px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.surface.pane};
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  color: inherit;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.background.button.onChrome.hover};
  }
`;
